// [목적] 폴리오 공유 버튼 — OG 메타 공유 URL 복사/네이티브 공유 + private 폴리오 공개 전환 안내
// [이유] FlowLine A1(0f5e9865): 공유 URL 은 BE OG 메타 페이지(GET /share/folios/:id,
//        apps/api folio-share.controller.ts) — API 오리진 + /share/folios/:id 로 조립.
//        navigator.share 지원 시 네이티브 공유 시트, 미지원이면 클립보드 복사 + 토스트.
//        visibility=private 이면 공유 URL 이 항상 404(존재 은닉, canViewFolio(actor=null)) 이므로
//        소유자에게는 PATCH visibility=link 전환 액션을 제공, 비소유자에게는 안내만 표시.
// [사이드 이팩트] 소유자 확인 다이얼로그에서 "전환하고 공유" 선택 시 PATCH /folios/:id 호출 →
//                folio 캐시(queryKeys.folio) invalidate.
// [수정 시 고려사항] 버튼의 시각 스타일은 호출부(FolioPage/FolioMobile)가 sx/children 으로 주입 —
//                   데스크톱·모바일 헤더 액션 바의 기존 시각 언어를 그대로 유지하기 위함(헤드리스 컴포넌트).
//                   apiBase 는 http.ts SoT — fetchWithEnvelope 와 동일한 same-origin 규칙 재사용.

import { useState, type ReactNode } from 'react';
import {
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  type SxProps,
  type Theme,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { tokens } from '@gangwoncanvas/ui';
import type { Folio } from '@gangwoncanvas/types';

import { apiBase } from '../../api/http.js';
import { patchFolio } from '../../api/folioClient.js';
import { queryKeys } from '../../api/queryKeys.js';
import { useToast } from '../feedback/Toast.js';

const c = tokens.color;
const L = 'light' as const;

/** GET /share/folios/:id (apps/api folio-share.controller.ts) 공유 URL 조립 */
export function buildFolioShareUrl(folioId: string): string {
  // [2026-09-17 메인 검수] apiBase 가 빈 문자열(same-origin 배포)이면 상대경로가 복사·공유되어 외부 앱에서
  // 열 수 없었다 — 현재 오리진 기준 절대 URL 로 해석(apiBase 가 절대 URL 이면 그대로 유지).
  return new URL(`${apiBase}/share/folios/${folioId}`, window.location.origin).href;
}

/**
 * [목적] navigator.share 지원 시 네이티브 공유, 아니면 클립보드 복사
 * [이유] A1: "navigator.share 지원 시 사용, 아니면 clipboard 복사 + 토스트"
 * [사이드 이팩트] 사용자가 네이티브 공유 시트를 취소(AbortError)하면 아무 알림도 띄우지 않음.
 * [수정 시 고려사항] navigator.share 자체가 실패(취소 아닌 에러)하면 클립보드 복사로 폴백.
 */
async function shareOrCopy(
  url: string,
  title: string,
  text: string | null,
): Promise<'shared' | 'copied' | 'failed' | 'cancelled'> {
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text: text ?? undefined, url });
      return 'shared';
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return 'cancelled';
      // 네이티브 공유 실패 — 클립보드 복사로 폴백 계속 진행
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    return 'copied';
  } catch {
    return 'failed';
  }
}

export interface FolioShareButtonProps {
  folio: Folio;
  isOwner: boolean;
  sx?: SxProps<Theme>;
  children: ReactNode;
}

/**
 * [목적] 공유 트리거 버튼 + private 전환 확인 다이얼로그
 * [이유] A1 — 시각 스타일은 호출부 주입(헤드리스), 공유/전환 로직만 캡슐화.
 * [사이드 이팩트] 위 shareOrCopy/patchFolio 참조.
 * [수정 시 고려사항] isOwner 는 호출부가 folio.ownerUserId 기준으로 계산해 전달.
 */
export function FolioShareButton({
  folio,
  isOwner,
  sx,
  children,
}: FolioShareButtonProps): JSX.Element {
  const { show } = useToast();
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function doShare(): Promise<void> {
    const url = buildFolioShareUrl(folio.id);
    const result = await shareOrCopy(url, folio.title, folio.subtitle);
    if (result === 'copied') show('공유 링크를 복사했어요.', 'success');
    if (result === 'failed') show('링크 복사에 실패했어요.', 'error');
  }

  const makeLinkVisible = useMutation({
    mutationFn: () => patchFolio(folio.id, { visibility: 'link' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.folio(folio.id) });
      setConfirmOpen(false);
      void doShare();
    },
    onError: () => show('공개 전환에 실패했어요. 잠시 후 다시 시도해 주세요.', 'error'),
  });

  function handleClick(): void {
    if (folio.visibility === 'private') {
      if (isOwner) {
        setConfirmOpen(true);
      } else {
        show('비공개 폴리오는 공유할 수 없어요.', 'warning');
      }
      return;
    }
    void doShare();
  }

  return (
    <>
      <ButtonBase onClick={handleClick} aria-label="공유" sx={sx}>
        {children}
      </ButtonBase>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="share-confirm-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          id="share-confirm-title"
          sx={{ fontSize: 15, fontWeight: 700, color: c.ink.high[L] }}
        >
          링크를 공개로 전환할까요?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13, color: c.ink.mid[L] }}>
            비공개 폴리오는 공유 링크로 열람할 수 없어요. 링크가 있는 사람만 볼 수 있도록 전환한 뒤
            공유합니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: `${tokens.spacing[4]}px`, pb: `${tokens.spacing[3]}px` }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            aria-label="공개 전환 취소"
            disabled={makeLinkVisible.isPending}
          >
            취소
          </Button>
          <Button
            onClick={() => makeLinkVisible.mutate()}
            variant="contained"
            aria-label="링크 공개로 전환하고 공유"
            disabled={makeLinkVisible.isPending}
            // [수정: 디자인 개편] 비활성일 때도 파란 배경이 그대로 남았다 — .Mui-disabled 로 회색 면으로 명시
            sx={{ bgcolor: c.brand[500][L], '&.Mui-disabled': { bgcolor: c.paper[2][L], color: c.ink.low[L] } }}
          >
            전환하고 공유
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
